import React, { useState } from 'react';

import SEOHelmet from '../../components/SEOHelmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PublicNavigation from '../../components/ui/PublicNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UnifiedFooter from '../../components/ui/UnifiedFooter';
import AIFAQChatbot from '../../components/AIFAQChatbot';

const ParticuliersLandingPage = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Quel est le délai de livraison Paris Abidjan ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le délai moyen de transport maritime en groupage entre Paris et Abidjan est de 3 à 5 semaines selon le départ."
        }
      },
      {
        "@type": "Question",
        "name": "Peut-on envoyer de la nourriture dans une barrique ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, les denrées alimentaires non périssables sont autorisées. Les produits frais ou interdits par la réglementation douanière ne sont pas acceptés."
        }
      },
      {
        "@type": "Question",
        "name": "Livrez-vous partout en Côte d\'Ivoire ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Oui, après arrivée au port d'Abidjan, une livraison peut être organisée vers les principales villes de Côte d'Ivoire."
        }
      },
      {
        "@type": "Question",
        "name": "Comment suivre mon colis vers Abidjan ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Chaque expédition dispose d\'un suivi en ligne accessible avec votre référence colis depuis notre page Suivi."
        }
      }
    ]
  };

  const structuredData = {
    localBusinessSchema: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "ADT Express",
      "url": "https://www.adtexpress.cloud",
      "areaServed": [
        {
          "@type": "AdministrativeArea",
          "name": "Île-de-France"
        },
        {
          "@type": "City",
          "name": "Abidjan"
        }
      ],
      "serviceType": "Transport maritime de colis et barriques France – Côte d\'Ivoire"
    },
    breadcrumbSchema: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": "https://www.adtexpress.cloud/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Particuliers",
          "item": "https://www.adtexpress.cloud/particuliers"
        }
      ]
    },
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Envoi de barriques et colis vers la Côte d\'Ivoire pour particuliers – Départ France",
    "provider": {
      "@type": "Organization",
      "name": "ADT Express",
      "url": "https://www.adtexpress.cloud"
    },
    "serviceType": "Transport maritime groupage Paris Abidjan",
    "areaServed": {
      "@type": "Country",
      "name": "Côte d\'Ivoire"
    }
  };

  const targetAudience = [
    { emoji: "👨‍👩‍👧", text: "Aux familles qui envoient régulièrement à Abidjan" },
    { emoji: "🎓", text: "Aux étudiants ivoiriens en France" },
    { emoji: "🧳", text: "Aux retours définitifs ou temporaires au pays" },
    { emoji: "📦", text: "Aux particuliers envoyant des effets personnels" },
    { emoji: "🛍️", text: "Aux envois de colis alimentaires autorisés" }
  ];

  const sendingSteps = [
    { number: "1️⃣", text: "Vous réservez votre envoi" },
    { number: "2️⃣", text: "Vous déposez votre barrique ou colis en région parisienne" },
    { number: "3️⃣", text: "Nous expédions en groupage maritime sécurisé" },
    { number: "4️⃣", text: "Réception au port d\'Abidjan avec suivi transparent" }
  ];

  const pricingOptions = [
    { icon: "Ship", title: "Transport simple", description: "Envoi de colis et petits volumes" },
    { icon: "Package", title: "Barrique noire", description: "Capacité 270L - Solution économique" },
    { icon: "Box", title: "Barrique bleue", description: "Capacité 220L - Plus résistante" }
  ];

  const whyChooseUs = [
    { icon: "Calendar", text: "2 départs par mois vers Abidjan" },
    { icon: "MapPin", text: "Dépôt en Île-de-France" },
    { icon: "Clock", text: "Suivi colis en temps réel" },
    { icon: "Headphones", text: "Assistance en France et en Côte d\'Ivoire" },
    { icon: "CheckCircle", text: "Transparence totale sur les délais" }
  ];

  const faqItems = [
    {
      question: "Quel est le délai Paris – Abidjan ?",
      answer: "Le délai moyen est de 3 à 4 semaines selon le départ maritime."
    },
    {
      question: "Peut-on envoyer de la nourriture dans une barrique ?",
      answer: "Oui, sous réserve des règles douanières en vigueur."
    },
    {
      question: "Quelle est la différence entre barrique noire et bleue ?",
      answer: "La capacité et le prix varient selon le modèle choisi."
    },
    {
      question: "Où déposer ma barrique en région parisienne ?",
      answer: "Notre point de dépôt est accessible en Île-de-France."
    },
    {
      question: "Combien coûte l\'envoi d\'une barrique vers Abidjan ?",
      answer: "Les tarifs dépendent du type de barrique et du transport choisi."
    }
  ];

  return (
    <>
      <SEOHelmet
        title="Envoi de barriques et colis vers la Côte d'Ivoire pour particuliers – Départ France | ADT Express"
        description="Transport maritime en groupage sécurisé Paris → Abidjan. Deux départs par mois. Suivi en ligne 24/7. Envoi barrique Côte d'Ivoire, envoyer colis Abidjan, groupage maritime Paris Abidjan."
        keywords={`envoi barrique Côte d'Ivoire, envoyer colis Abidjan, transport maritime particulier France Abidjan, groupage maritime Paris Abidjan, dépôt barrique Île-de-France, envoyer barrique Côte d'Ivoire, groupage maritime, tarif envoi Abidjan`}
        canonicalUrl="https://www.adtexpress.cloud/particuliers"
        structuredData={[structuredData, faqSchema]}
      />
      <div className="min-h-screen bg-white">
        <PublicNavigation />

        {/* 🟢 HERO SECTION */}
        <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20 px-4 overflow-hidden pt-44">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1578575437130-527eed3abbec?q=80&w=2070&auto=format&fit=crop')"
            }}
          />
          <div className="absolute inset-0 bg-black/60" />
          
          <div className="relative z-10 container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Envoi de barriques et colis vers la Côte d&apos;Ivoire pour particuliers – Départ France
              </h1>
              <p className="text-xl md:text-2xl text-gray-100 mb-6 max-w-4xl mx-auto">
                Transport maritime en groupage sécurisé Paris → Abidjan. Deux départs par mois. Suivi en ligne 24/7.
              </p>
              <p className="text-lg md:text-xl text-gray-200 mb-8 max-w-4xl mx-auto leading-relaxed">
                Envoyer une barrique ou un colis à Abidjan, ce n&apos;est pas simplement expédier des marchandises. C&apos;est soutenir sa famille, préparer un retour au pays, envoyer des effets personnels ou des produits essentiels en toute confiance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/home-tracking">
                  <Button 
                    size="lg" 
                    className="bg-white text-[#16A34A] hover:bg-gray-100 font-semibold shadow-lg"
                  >
                    <Icon name="MapPin" className="mr-2" size={20} />
                    Suivre mon colis
                  </Button>
                </Link>
                <Link to="/commander">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 font-semibold shadow-lg"
                  >
                    <Icon name="Package" className="mr-2" size={20} />
                    Envoyer une barrique
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 🟢 SECTION AUTORITÉ GÉOGRAPHIQUE */}
<section className="py-16 bg-gray-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-10">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
        Envoi de barrique Côte d’Ivoire depuis Paris et l’Île-de-France
      </h2>
      <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
        ADT Express organise le transport maritime en groupage depuis la région parisienne vers Abidjan.
        Nos clients viennent de Paris (75), Essonne (91), Seine-Saint-Denis (93), 
        Val-de-Marne (94), Hauts-de-Seine (92), Yvelines (78) et de toute l’Île-de-France.
      </p>
      <p className="text-lg text-gray-700 max-w-4xl mx-auto mt-6 leading-relaxed">
        Dépôt accessible, accompagnement personnalisé et départs réguliers vers le port d’Abidjan.
        Nous facilitons l’envoi de barriques et colis vers la Côte d’Ivoire pour les particuliers
        souhaitant soutenir leur famille ou préparer un retour au pays.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
      <div className="bg-white rounded-xl p-6 border border-green-100 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-2">📍 Dépôt en Île-de-France</h3>
        <p className="text-gray-600 text-sm">
          Point de dépôt accessible en région parisienne pour vos barriques et colis.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 border border-green-100 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-2">🚢 Départs réguliers vers Abidjan</h3>
        <p className="text-gray-600 text-sm">
          Deux départs maritimes par mois en groupage sécurisé vers le port d’Abidjan.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 border border-green-100 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-2">📦 Spécialiste diaspora</h3>
        <p className="text-gray-600 text-sm">
          Service conçu pour les familles ivoiriennes vivant en France.
        </p>
      </div>
    </div>
  </div>
</section>

        {/* 🔥 SECTION ÉMOTION DIASPORA */}
<section className="py-16 bg-gradient-to-br from-green-50 to-white">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center"
    >
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
        Envoyer plus qu’un colis. Envoyer un lien.
      </h2>

      <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-8 leading-relaxed">
        Derrière chaque barrique envoyée vers Abidjan, il y a une histoire :
        une mère qui attend, un frère qui compte sur vous, un retour au pays
        qui se prépare. Nous comprenons ce que représente un envoi vers la
        Côte d’Ivoire. C’est pourquoi nous avons structuré un transport
        maritime fiable, transparent et pensé pour les familles ivoiriennes
        vivant en France.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100">
          <h3 className="font-semibold text-gray-900 mb-2">
            🇫🇷 Départ France sécurisé
          </h3>
          <p className="text-gray-600 text-sm">
            Dépôt en Île-de-France avec départ maritime structuré vers Abidjan.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100">
          <h3 className="font-semibold text-gray-900 mb-2">
            🇨🇮 Arrivée Abidjan fiable
          </h3>
          <p className="text-gray-600 text-sm">
            Réception organisée au port d’Abidjan avec suivi transparent.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100">
          <h3 className="font-semibold text-gray-900 mb-2">
            📦 Suivi en ligne 24/7
          </h3>
          <p className="text-gray-600 text-sm">
            Consultez l’avancement de votre expédition à tout moment.
          </p>
        </div>
      </div>
    </motion.div>
  </div>
</section>
{/* ⭐ SECTION PREUVE SOCIALE */}
<section className="py-16 bg-white">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center mb-12"
    >
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        Ils nous font confiance pour leurs envois vers Abidjan
      </h2>
      <p className="text-lg text-gray-600 max-w-3xl mx-auto">
        Des familles et particuliers en France expédient régulièrement leurs barriques et colis avec ADT Express.
      </p>
    </motion.div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      
      <div className="bg-gray-50 rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="text-yellow-500 mb-3">★★★★★</div>
        <p className="text-gray-700 text-sm leading-relaxed mb-4">
          “J’envoie tous les 3 mois pour ma famille à Abidjan. Le suivi en ligne me rassure énormément.”
        </p>
        <p className="font-semibold text-gray-900 text-sm">Moussa B. – Paris</p>
      </div>

      <div className="bg-gray-50 rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="text-yellow-500 mb-3">★★★★★</div>
        <p className="text-gray-700 text-sm leading-relaxed mb-4">
          “Transport clair, délais respectés, communication fluide entre la France et la Côte d’Ivoire.”
        </p>
        <p className="font-semibold text-gray-900 text-sm">Aïcha K. – Île-de-France</p>
      </div>

      <div className="bg-gray-50 rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="text-yellow-500 mb-3">★★★★★</div>
        <p className="text-gray-700 text-sm leading-relaxed mb-4">
          “J’ai testé plusieurs transporteurs. ADT Express reste le plus transparent sur le suivi.”
        </p>
        <p className="font-semibold text-gray-900 text-sm">Yao D. – Essonne</p>
      </div>

    </div>
  </div>
</section>
{/* 🚀 CTA INTERMÉDIAIRE CONVERSION */}
<section className="py-16 bg-green-600 text-white">
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <h2 className="text-3xl md:text-4xl font-bold mb-6">
        Prêt à envoyer votre barrique vers Abidjan ?
      </h2>

      <p className="text-lg md:text-xl text-green-100 mb-8 max-w-3xl mx-auto">
        Départs réguliers depuis la France. Suivi transparent. 
        Une solution fiable pour soutenir votre famille en Côte d’Ivoire.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/commander">
          <Button
            size="lg"
            className="bg-white text-green-700 hover:bg-gray-100 font-semibold shadow-lg"
          >
            Envoyer une barrique maintenant
          </Button>
        </Link>

        <Link to="/home-tracking">
          <Button
            size="lg"
            variant="outline"
            className="border-2 border-white text-white hover:bg-white/10 font-semibold"
          >
            Suivre un colis existant
          </Button>
        </Link>
      </div>
    </motion.div>
  </div>
</section>
{/* 📦 SECTION SEO – QUE PEUT-ON ENVOYER ? */}
<section className="py-16 bg-gray-50">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center mb-12"
    >
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
        Que peut-on envoyer dans une barrique vers Abidjan ?
      </h2>
      <p className="text-lg text-gray-600 max-w-3xl mx-auto">
        Nos clients particuliers expédient régulièrement :
      </p>
    </motion.div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">👕 Vêtements & effets personnels</h3>
        <p className="text-gray-600 text-sm">
          Habits, chaussures, linge de maison, sacs, objets personnels.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">🛍️ Produits du quotidien</h3>
        <p className="text-gray-600 text-sm">
          Produits non périssables autorisés, articles d’hygiène, fournitures.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">📚 Fournitures scolaires</h3>
        <p className="text-gray-600 text-sm">
          Livres, cahiers, matériel éducatif pour la famille en Côte d’Ivoire.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">🔌 Petits appareils</h3>
        <p className="text-gray-600 text-sm">
          Équipements domestiques légers autorisés selon réglementation.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">🧳 Effets de retour définitif</h3>
        <p className="text-gray-600 text-sm">
          Objets personnels lors d’un retour au pays.
        </p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">📦 Colis familiaux</h3>
        <p className="text-gray-600 text-sm">
          Envois réguliers à destination d’Abidjan et principales villes.
        </p>
      </div>

    </div>

    <div className="mt-10 text-center">
      <p className="text-gray-600 text-sm max-w-3xl mx-auto">
        Certains produits sont soumis à réglementation douanière. Nous vous accompagnons pour éviter toute erreur lors de votre envoi vers la Côte d’Ivoire.
      </p>
    </div>
  </div>
</section>



        {/* 🟢 SECTION 2 – À QUI S'ADRESSE NOTRE SERVICE ? */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Un service pensé pour la diaspora ivoirienne en France
              </h2>
              <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
                Nos solutions d'envoi vers la Côte d'Ivoire s'adressent :
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {targetAudience?.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-green-50 to-gray-50 rounded-xl p-6 border border-green-100 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex items-start gap-4">
                    <span className="text-4xl flex-shrink-0">{item?.emoji}</span>
                    <p className="text-gray-800 font-medium leading-relaxed pt-2">{item?.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center"
            >
              <p className="text-lg text-gray-700 max-w-4xl mx-auto">
                Nous accompagnons les particuliers en région parisienne et en Île-de-France pour leurs expéditions maritimes vers Abidjan.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 🟡 SECTION 3 – COMMENT ENVOYER UNE BARRIQUE À ABIDJAN ? */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Comment envoyer une barrique depuis Paris vers Abidjan ?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Étapes simples
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
              {sendingSteps?.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200"
                >
                  <div className="text-4xl mb-4">{step?.number}</div>
                  <p className="text-gray-800 font-medium leading-relaxed">{step?.text}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center"
            >
              <p className="text-lg text-gray-700 max-w-4xl mx-auto">
                Chaque expédition bénéficie d'un suivi en ligne accessible 24h/24.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 🔵 SECTION 4 – NOS TARIFS POUR PARTICULIERS */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Tarifs transport maritime particuliers France – Côte d'Ivoire
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
                Nous proposons :
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              {pricingOptions?.map((option, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-green-50 to-gray-50 rounded-xl p-8 border border-green-100 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <Icon name={option?.icon} size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{option?.title}</h3>
                  <p className="text-gray-700 text-center leading-relaxed">{option?.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center"
            >
              <p className="text-lg text-gray-700 mb-6 max-w-4xl mx-auto">
                Des tarifs transparents sans frais cachés. Départs réguliers depuis la France vers Abidjan.
              </p>
              <Link to="/tarifs">
                <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white font-semibold">
                  Voir tous les tarifs
                  <Icon name="ArrowRight" className="ml-2" size={20} />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* 🟣 SECTION 5 – POURQUOI CHOISIR ADT EXPRESS ? */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Transport maritime sécurisé vers Abidjan
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {whyChooseUs?.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={24} className="text-green-600" />
                    </div>
                    <p className="text-gray-800 font-medium leading-relaxed pt-2">{item?.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center"
            >
              <p className="text-lg text-gray-700 max-w-4xl mx-auto">
                Nous facilitons l'envoi de barriques Côte d'Ivoire depuis Paris avec une logistique structurée et fiable.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 🟢 SECTION – POURQUOI ADT EXPRESS EST LE CHOIX DE CONFIANCE */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Pourquoi ADT Express est le choix de confiance pour envoyer une barrique ou un colis vers Abidjan ?
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Icon name="MapPin" size={24} className="text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Spécialiste France – Côte d'Ivoire
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Contrairement aux transporteurs généralistes, nous sommes spécialisés dans le groupage maritime depuis Paris et l'Île-de-France vers Abidjan. Nous connaissons parfaitement les formalités et les réalités du terrain, pour que votre famille reçoive vos envois en toute sérénité.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Icon name="Calendar" size={24} className="text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Départs réguliers garantis
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Deux départs par mois depuis la région parisienne vers Abidjan. Pas d'attente indéfinie. Vos colis et barriques partent selon un calendrier clair et annoncé, parce que votre famille compte sur vous.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Icon name="Eye" size={24} className="text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Suivi transparent
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Chaque expédition dispose d'un suivi en ligne avec référence. Vous savez où se trouve votre envoi entre Paris et Abidjan, entre la France et la Côte d'Ivoire. Une tranquillité d'esprit pour vous et vos proches.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  <Icon name="DollarSign" size={24} className="text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Tarifs sans surprise
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Des prix clairs, annoncés à l'avance. Aucun frais caché à l'arrivée au port d'Abidjan en Côte d'Ivoire. Votre budget est respecté, votre confiance préservée.
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center"
            >
              <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-8">
                ADT Express facilite l'envoi de barriques et colis vers Abidjan depuis Paris et l'Île-de-France avec une logistique structurée, fiable et pensée pour les familles qui veulent rester proches de leurs proches.
              </p>
              <Link to="/commander">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold shadow-lg"
                >
                  Envoyer ma barrique vers Abidjan
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* 🟤 SECTION 6 – FAQ PARTICULIERS (SEO FORT) */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Questions fréquentes – Envoi colis Côte d'Ivoire
              </h2>
            </motion.div>

            <div className="space-y-4">
              {faqItems?.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200"
                >
                  <button
                    onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors duration-200"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq?.question}</span>
                    <Icon 
                      name={expandedFAQ === index ? "ChevronUp" : "ChevronDown"} 
                      size={24} 
                      className="text-green-600 flex-shrink-0" 
                    />
                  </button>
                  {expandedFAQ === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 pb-5"
                    >
                      <p className="text-gray-600 leading-relaxed">{faq?.answer}</p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* AI-Powered FAQ Chatbot */}
        <AIFAQChatbot />

        <UnifiedFooter />
      </div>
    </>
  );
};

export default ParticuliersLandingPage;